import type { Attachment } from 'svelte/attachments'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'textarea',
  'input:not([type="hidden"])',
  'select',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([disabled]):not([tabindex="-1"])`)
  .join(', ')

function focusableElements(node: HTMLElement): HTMLElement[] {
  // getClientRects() drops `display: none` elements: they match the selector but can't take focus.
  return [...node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    (element) => element.getClientRects().length > 0,
  )
}

export function focusTrap(active = true, focusOptions?: FocusOptions): Attachment<HTMLElement> {
  return (node) => {
    if (!active) {
      return
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') {
        return
      }

      const elements = focusableElements(node)
      if (elements.length === 0) {
        event.preventDefault()
        return
      }

      const forward = !event.shiftKey
      const first = elements[0]
      const last = elements[elements.length - 1]
      const atEdge = document.activeElement === (forward ? last : first)
      const hasEscaped = !node.contains(document.activeElement)

      if (atEdge || hasEscaped) {
        event.preventDefault()
        ;(forward ? first : last).focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)

    const [first] = focusableElements(node)
    ;(first ?? node).focus(focusOptions)

    return () => {
      node.removeEventListener('keydown', onKeyDown)
    }
  }
}
